import React, { useState, useEffect, useMemo } from 'react';
import { useCart, cartLineKey } from '../context/CartContext';
import { settingsAPI, ordersAPI } from '../services/api';
import { DEFAULT_WHATSAPP_DIGITS, normalizeWhatsAppDigits } from '../utils/whatsapp';
import {
  buildWhatsAppOrderText,
  cartItemsToOrderPayload,
  vehicleTitleFromItem,
} from '../utils/whatsappMessage';
import CheckoutModal from './CheckoutModal';
import './CartDrawer.css';

function groupItemsByVehicle(items) {
  const map = new Map();
  for (const item of items) {
    const title = vehicleTitleFromItem(item);
    if (!map.has(title)) map.set(title, []);
    map.get(title).push(item);
  }
  return [...map.entries()];
}

const CartDrawer = ({ isOpen, onClose }) => {
  const { items, removeFromCart, updateQuantity, clearCart } = useCart();
  const [whatsappNumber, setWhatsappNumber] = useState(DEFAULT_WHATSAPP_DIGITS);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      settingsAPI
        .getSettings()
        .then((res) => {
          const raw = res.data && res.data.whatsapp_number;
          setWhatsappNumber(normalizeWhatsAppDigits(raw));
        })
        .catch(() => setWhatsappNumber(DEFAULT_WHATSAPP_DIGITS));
    }
  }, [isOpen]);

  const cartTotal = items.reduce(
    (sum, i) => sum + (Number(i.price) || 0) * (i.quantity || 1),
    0
  );

  const grouped = useMemo(() => groupItemsByVehicle(items), [items]);

  const openWhatsAppWithText = (message, popup) => {
    const encoded = encodeURIComponent(message);
    const fullNum = normalizeWhatsAppDigits(whatsappNumber).replace(/\D/g, '');
    const url = `https://wa.me/${fullNum}?text=${encoded}`;
    if (popup && !popup.closed) {
      popup.location.href = url;
      return;
    }
    window.open(url, '_blank');
  };

  const handleCheckoutConfirm = async ({ phone, address }, popup) => {
    const message = buildWhatsAppOrderText(items, { phone, address });
    setOrderLoading(true);
    try {
      await ordersAPI.create({
        phone,
        address,
        whatsapp_text: message,
        items: cartItemsToOrderPayload(items),
      });
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || e.response?.data?.phone?.[0] || 'تعذر حفظ الطلب. حاول مرة أخرى.');
      setOrderLoading(false);
      try { if (popup && !popup.closed) popup.close(); } catch {}
      return;
    }
    setOrderLoading(false);
    setCheckoutOpen(false);
    openWhatsAppWithText(message, popup);
    clearCart();
    onClose();
  };

  const handleSendWhatsApp = () => {
    if (items.length === 0) {
      alert('السلة فارغة. أضف قطعاً أولاً.');
      return;
    }
    setCheckoutOpen(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={() => !orderLoading && setCheckoutOpen(false)}
        onConfirm={handleCheckoutConfirm}
        loading={orderLoading}
        title="إتمام الطلب"
      />
      <div className="cart-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="cart-drawer" role="dialog" aria-label="سلة الطلبات">
        <div className="cart-drawer-header">
          <h2 className="cart-drawer-title">سلة الطلبات</h2>
          <button type="button" className="cart-drawer-close" onClick={onClose} aria-label="إغلاق">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="cart-drawer-body">
          {items.length === 0 ? (
            <div className="cart-drawer-empty">
              <i className="fas fa-shopping-cart" />
              <p>السلة فارغة</p>
              <span>أضف القطع من صفحات المنتجات</span>
            </div>
          ) : (
            <div className="cart-drawer-groups">
              {grouped.map(([vehicleTitle, groupItems]) => (
                <section key={vehicleTitle} className="cart-drawer-group">
                  <h3 className="cart-drawer-group-title">{vehicleTitle}</h3>
                  <ul className="cart-drawer-list">
                    {groupItems.map((item) => (
                      <li key={cartLineKey(item)} className="cart-drawer-item">
                        <div className="cart-drawer-item-info">
                          <span className="cart-drawer-item-name">{item.name_ar || item.name || 'قطعة'}</span>
                          {item.part_number && (
                            <span className="cart-drawer-item-partno">رقم: {item.part_number}</span>
                          )}
                          {item.price != null && item.price !== '' && (
                            <span className="cart-drawer-item-price">{item.price} د.ل</span>
                          )}
                        </div>
                        <div className="cart-drawer-item-actions">
                          <div className="cart-drawer-qty">
                            <button
                              type="button"
                              className="cart-drawer-qty-btn"
                              onClick={() =>
                                updateQuantity(item, Math.max(1, (item.quantity || 1) - 1))
                              }
                              aria-label="تقليل"
                            >
                              −
                            </button>
                            <span className="cart-drawer-qty-num">{item.quantity || 1}</span>
                            <button
                              type="button"
                              className="cart-drawer-qty-btn"
                              onClick={() => updateQuantity(item, (item.quantity || 1) + 1)}
                              aria-label="زيادة"
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            className="cart-drawer-remove"
                            onClick={() => removeFromCart(item)}
                            aria-label="حذف"
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
        {items.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-drawer-total" role="status">
              <span className="cart-drawer-total-label">المجموع الكلي</span>
              <span className="cart-drawer-total-value">{cartTotal.toFixed(2)} د.ل</span>
            </div>
            <button
              type="button"
              className="cart-drawer-clear"
              onClick={() => {
                if (window.confirm('تفريغ السلة؟')) clearCart();
              }}
            >
              تفريغ السلة
            </button>
            <button type="button" className="cart-drawer-whatsapp" onClick={handleSendWhatsApp}>
              <span className="whatsapp-icon">📱</span>
              إرسال الطلب عبر واتساب
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
